import { PERMISSIONS } from "@shared/constants/permissions";

import type { UserData } from "@/api/auth";

/**
 * How a principal relates to claimant PII — the ONE client-side encoding of the server's rule
 * (/ai/chat, RAP-693/696). Every reader (the auth store's funder + chat-hidden gates, the chat
 * context picker, Mixpanel's `user_kind`) derives from this, so a change to the rule lands once.
 *
 * - `external` — no org (view-assigned): PII is governed by their view, never by a permission.
 * - `full`     — an org member holding `view_pii`.
 * - `funder`   — a referring-portal firm member without `view_pii`: redacted firm chat only.
 * - `none`     — any other org member without `view_pii`: the server 403s all of their chat.
 *
 * Store-free on purpose: tracking calls it with the raw /auth/me payload before the store settles.
 */
export type PiiLane = "external" | "full" | "funder" | "none";

export type PiiLaneUser = Pick<
  UserData,
  "organization" | "organizationId" | "permissions" | "referringPortalEnabled"
>;

export function piiLaneFor(user: null | PiiLaneUser | undefined): PiiLane {
  if (!user) return "external";
  const viewPii = (user.permissions ?? []).includes(PERMISSIONS.VIEW_PII);
  // The portal entitlement is a firm's, so a portal user always has an org; it is checked first
  // only so a funder never depends on which org field a payload happens to carry. Absent
  // `referringPortalEnabled` (the slim login payload) reads as OFF, as everywhere else.
  if (user.referringPortalEnabled === true && !viewPii) return "funder";
  if (!user.organizationId && !user.organization) return "external";
  return viewPii ? "full" : "none";
}
